import { useQuery } from '@tanstack/react-query';
import { listAdminTransactions, getAdminTransaction } from '@/lib/api/admin-transactions';
import type { ListAdminTransactionsParams, TransactionStatus } from '@/types/admin-transaction';

export const adminTransactionKeys = {
  all: ['admin-transactions'] as const,
  lists: () => [...adminTransactionKeys.all, 'list'] as const,
  list: (params?: ListAdminTransactionsParams) =>
    [...adminTransactionKeys.lists(), params] as const,
  details: () => [...adminTransactionKeys.all, 'detail'] as const,
  detail: (id: string) => [...adminTransactionKeys.details(), id] as const,
};

const TERMINAL_STATUSES: TransactionStatus[] = [
  'charged', 'renewed', 'failed', 'cancelled', 'expired',
];

export function useAdminTransactions(params?: ListAdminTransactionsParams) {
  return useQuery({
    queryKey: adminTransactionKeys.list(params),
    queryFn: () => listAdminTransactions(params),
    staleTime: 30_000,
  });
}

/**
 * Hook to fetch a single transaction by ID.
 * Spec 0027: Optional merchantId hint for platform_admin Facade routing.
 * Query-Key stays without merchantId — same data regardless of routing path.
 */
export function useAdminTransaction(transactionId: string | undefined, merchantId?: string) {
  return useQuery({
    queryKey: adminTransactionKeys.detail(transactionId ?? ''),
    queryFn: () => getAdminTransaction(transactionId!, merchantId),
    enabled: !!transactionId,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && !TERMINAL_STATUSES.includes(status)) return 5_000;
      return false;
    },
  });
}
